import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth';
import { Gallery, Item } from 'react-photoswipe-gallery';
import { RowsPhotoAlbum } from 'react-photo-album';
import 'react-photo-album/rows.css';
import 'photoswipe/dist/photoswipe.css';
import { UserMenu } from '../../components/UserMenu';
import type { Profile, ProfilePhoto, ModelingStyle, LanguageCode, PhotoType, UpcomingSession } from '@modelcast/types';
import { LANGUAGES } from '@modelcast/types';
import { COUNTRIES } from '../model-signup/countries';
import { MODELING_STYLE_OPTIONS } from '../model-signup/modelSignup.types';
import { Wordmark } from '../../components/Wordmark';
import { ModelPrivacyDefaults } from './ModelPrivacyDefaults';
import { SharedFooter } from '../../components/SharedFooter';
import { useCopy } from '../../shared/copy/CopyProvider';
import { useCurrentProfile } from '../../shared/CurrentProfileProvider';
import { formatMoney, formatMoneyCompact } from '../../shared/money';
import { authHeaders, signInMessageForAuthStatus } from '../../shared/authHeaders';
import { getDisplayNameValidationMessage } from '../../shared/validation/displayName';
import { AccountDeletionPanel } from '../account-deletion/AccountDeletionPanel';

const LANGUAGE_OPTIONS = Object.entries(LANGUAGES) as [LanguageCode, string][];

interface ManagedReferencePack {
  id: string;
  title: string;
  status: 'draft' | 'ready';
  accessType: 'sellable' | 'free' | 'private';
  showOnProfile: boolean;
  priceCents: number | null;
  currency: string;
  room: {
    code: string;
    name: string;
  };
  updatedAt: string;
}

interface ReferencePackAccessRequest {
  id: string;
  status: 'pending' | 'approved' | 'denied' | 'purchased';
  intendedUse: string;
  requestedAccessExpiresOn: string | null;
  requestedPriceCents: number | null;
  approvedPriceCents: number | null;
  approvedCurrency: string | null;
  sellerNote: string | null;
  createdAt: string | null;
  decidedAt: string | null;
  product: {
    id: string;
    title: string;
    priceCents: number | null;
    currency: string;
  };
  room: {
    code: string;
    name: string;
  };
  buyerProfile: {
    id: string;
    displayName: string | null;
  };
}

interface AccessRequestDecisionDraft {
  price: string;
  note: string;
}

type DraftProfilePhoto = ProfilePhoto & {
  file?: File;
  localUrl?: string;
  rotationDegrees?: number;
};

// Country flag emoji from country code
function getCountryFlag(code: string): string {
  const codePoints = code
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

function formatCents(cents: number | null | undefined, currency: string | null | undefined = 'USD') {
  if (!cents || cents <= 0) return 'Free';
  return formatMoneyCompact(cents, currency);
}

function centsToPriceInput(cents: number | null | undefined) {
  if (!cents || cents <= 0) return '';
  return (cents / 100).toFixed(2);
}

function parsePriceCents(value: string) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.round(parsed * 100);
}

export function EditProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getToken } = useAuth();
  const { refresh: refreshCurrentProfile } = useCurrentProfile();
  const { t } = useCopy();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [upcomingSessions, setUpcomingSessions] = useState<UpcomingSession[]>([]);
  const [referencePacks, setReferencePacks] = useState<ManagedReferencePack[]>([]);
  const [accessRequests, setAccessRequests] = useState<ReferencePackAccessRequest[]>([]);
  const [loadingAccessRequests, setLoadingAccessRequests] = useState(false);
  const [accessRequestDrafts, setAccessRequestDrafts] = useState<Record<string, AccessRequestDecisionDraft>>({});
  const [reviewingRequestId, setReviewingRequestId] = useState<string | null>(null);
  const [deletingReferencePackId, setDeletingReferencePackId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadingSpacePhotos, setUploadingSpacePhotos] = useState(false);
  const [uploadingArtwork, setUploadingArtwork] = useState(false);
  const [showSuccess, setShowSuccess] = useState(location.state?.success === true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const spaceFileInputRef = useRef<HTMLInputElement>(null);
  const artworkFileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [instagram, setInstagram] = useState('');
  const [website, setWebsite] = useState('');
  const [country, setCountry] = useState('');
  const [isModel, setIsModel] = useState(false);
  const [isArtist, setIsArtist] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const [modelingStyles, setModelingStyles] = useState<ModelingStyle[]>([]);
  const [languages, setLanguages] = useState<LanguageCode[]>([]);
  const [languageSearch, setLanguageSearch] = useState('');
  const [defaultShowIdentity, setDefaultShowIdentity] = useState(true);
  const [cameraEquipment, setCameraEquipment] = useState('');
  const [draftPhotos, setDraftPhotos] = useState<DraftProfilePhoto[]>([]);
  const [draftSpacePhotos, setDraftSpacePhotos] = useState<DraftProfilePhoto[]>([]);
  const [draftArtworkPhotos, setDraftArtworkPhotos] = useState<DraftProfilePhoto[]>([]);
  const objectUrlsRef = useRef<string[]>([]);

  // Tab state from URL
  type ProfileTab = 'general' | 'model' | 'reference-packs' | 'artist' | 'finances' | 'account';
  const validTabs: ProfileTab[] = ['general', 'model', 'reference-packs', 'artist', 'finances', 'account'];
  const tabParam = searchParams.get('tab');
  const activeTab: ProfileTab = validTabs.includes(tabParam as ProfileTab) ? (tabParam as ProfileTab) : 'general';

  const setActiveTab = (tab: ProfileTab) => {
    setSearchParams({ tab }, { replace: true });
  };

  // Financial data
  interface TicketPurchase {
    id: string;
    roomId: string;
    roomName: string;
    roomCode: string;
    amount: number;
    status: string;
    createdAt: string;
    scheduledAt: string | null;
  }
  interface PendingPayoutSummary {
    total: number;
    count: number;
    byCurrency: Array<{ currency: string; total: number; count: number }>;
  }
  const [ticketPurchases, setTicketPurchases] = useState<TicketPurchase[]>([]);
  const [pendingPayouts, setPendingPayouts] = useState<PendingPayoutSummary>({ total: 0, count: 0, byCurrency: [] });
  const [loadingFinances, setLoadingFinances] = useState(false);

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  // Clear success message after a few seconds
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => setShowSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  const fetchProfile = async () => {
    try {
      const token = await getToken();
      const response = await fetch('/api/profiles/me', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 404) {
        setProfile(null);
        setUpcomingSessions([]);
        setReferencePacks([]);
        setAccessRequests([]);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to load profile');
      }

      const data = await response.json();
      applyProfileToDraft(data.profile);

      // Fetch upcoming sessions
      try {
        const sessionsResponse = await fetch(`/api/profiles/${data.profile.id}/sessions`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (sessionsResponse.ok) {
          const sessionsData = await sessionsResponse.json();
          setUpcomingSessions(sessionsData.sessions || []);
        }
      } catch (sessionsErr) {
        console.error('Error fetching sessions:', sessionsErr);
        // Don't fail the whole page for sessions
      }

      try {
        const packsResponse = await fetch('/api/products/reference-packs/mine', {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (packsResponse.ok) {
          const packsData = await packsResponse.json();
          setReferencePacks(packsData.products || []);
        }
      } catch (packsErr) {
        console.error('Error fetching reference packs:', packsErr);
      }

      await fetchAccessRequests(token);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccessRequests = async (existingToken?: string | null) => {
    setLoadingAccessRequests(true);
    try {
      const token = existingToken ?? await getToken();
      const response = await fetch('/api/products/reference-packs/access-requests', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        const requests: ReferencePackAccessRequest[] = data.requests || [];
        setAccessRequests(requests);
        setAccessRequestDrafts((current) => {
          const next = { ...current };
          for (const request of requests) {
            if (!next[request.id]) {
              next[request.id] = {
                price: centsToPriceInput(request.approvedPriceCents ?? request.requestedPriceCents ?? request.product.priceCents),
                note: request.sellerNote ?? '',
              };
            }
          }
          return next;
        });
      }
    } catch (requestsErr) {
      console.error('Error fetching reference pack access requests:', requestsErr);
    } finally {
      setLoadingAccessRequests(false);
    }
  };

  const updateAccessRequestDraft = (
    requestId: string,
    field: keyof AccessRequestDecisionDraft,
    value: string,
  ) => {
    setAccessRequestDrafts((current) => ({
      ...current,
      [requestId]: {
        price: current[requestId]?.price ?? '',
        note: current[requestId]?.note ?? '',
        [field]: value,
      },
    }));
  };

  const approveAccessRequest = async (request: ReferencePackAccessRequest) => {
    const draft = accessRequestDrafts[request.id] ?? {
      price: centsToPriceInput(request.requestedPriceCents ?? request.product.priceCents),
      note: '',
    };
    const priceCents = parsePriceCents(draft.price);

    if (!priceCents) {
      window.alert(t('referencePacks.modelQueue.enterApprovedPrice'));
      return;
    }

    setReviewingRequestId(request.id);
    try {
      const token = await getToken();
      const response = await fetch(`/api/products/reference-packs/access-requests/${request.id}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceCents,
          note: draft.note.trim() || null,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || t('referencePacks.modelQueue.failedToApprove'));
      }

      await fetchAccessRequests(token);
    } catch (err) {
      console.error('Error approving reference pack request:', err);
      window.alert(err instanceof Error ? err.message : t('referencePacks.modelQueue.failedToApprove'));
    } finally {
      setReviewingRequestId(null);
    }
  };

  const denyAccessRequest = async (request: ReferencePackAccessRequest) => {
    const draft = accessRequestDrafts[request.id] ?? { price: '', note: '' };

    setReviewingRequestId(request.id);
    try {
      const token = await getToken();
      const response = await fetch(`/api/products/reference-packs/access-requests/${request.id}/deny`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          note: draft.note.trim() || null,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || t('referencePacks.modelQueue.failedToDeny'));
      }

      await fetchAccessRequests(token);
    } catch (err) {
      console.error('Error denying reference pack request:', err);
      window.alert(err instanceof Error ? err.message : t('referencePacks.modelQueue.failedToDeny'));
    } finally {
      setReviewingRequestId(null);
    }
  };

  const deleteReferencePack = async (pack: ManagedReferencePack) => {
    const confirmed = window.confirm(
      `Permanently delete "${pack.title}"?\n\nAnyone with access will lose access to this reference pack. This cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingReferencePackId(pack.id);
    try {
      const token = await getToken();

      const response = await fetch(`/api/products/reference-packs/${pack.id}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const authMessage = signInMessageForAuthStatus(response, 'Please sign in again to delete this reference pack');
        if (authMessage) throw new Error(authMessage);
        throw new Error(data.error || 'Failed to delete reference pack');
      }

      setReferencePacks((current) => current.filter((item) => item.id !== pack.id));
      setAccessRequests((current) => current.filter((request) => request.product.id !== pack.id));
    } catch (err) {
      console.error('Error deleting reference pack:', err);
      window.alert(err instanceof Error ? err.message : 'Failed to delete reference pack');
    } finally {
      setDeletingReferencePackId(null);
    }
  };

  const applyProfileToDraft = (nextProfile: Profile) => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrlsRef.current = [];

    setProfile(nextProfile);
    setDisplayName(nextProfile.displayName);
    setUsername(nextProfile.username || '');
    setBio(nextProfile.bio || '');
    setInstagram(nextProfile.instagram || '');
    setWebsite(nextProfile.website || '');
    setCountry(nextProfile.country || '');
    setIsModel(nextProfile.isModel);
    setIsArtist(nextProfile.isArtist);
    setIsHost(nextProfile.isHost);
    setModelingStyles(nextProfile.modelingStyles || []);
    setLanguages(nextProfile.languages || []);
    setDefaultShowIdentity(nextProfile.defaultShowIdentity ?? true);
    setCameraEquipment(nextProfile.cameraEquipment || '');
    setDraftPhotos(nextProfile.photos || []);
    setDraftSpacePhotos(nextProfile.spacePhotos || []);
    setDraftArtworkPhotos(nextProfile.artworkPhotos || []);
  };

  const fetchFinances = async () => {
    setLoadingFinances(true);
    try {
      const token = await getToken();

      // Fetch ticket purchases
      const ticketsResponse = await fetch('/api/payments/my-purchases', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (ticketsResponse.ok) {
        const ticketsData = await ticketsResponse.json();
        setTicketPurchases(ticketsData.purchases || []);
      }

      // Fetch pending payouts
      const payoutsResponse = await fetch('/api/stripe/pending-payouts', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (payoutsResponse.ok) {
        const payoutsData = await payoutsResponse.json();
        setPendingPayouts({
          total: payoutsData.total || 0,
          count: payoutsData.count || 0,
          byCurrency: Array.isArray(payoutsData.byCurrency) ? payoutsData.byCurrency : [],
        });
      }
    } catch (err) {
      console.error('Error fetching finances:', err);
    } finally {
      setLoadingFinances(false);
    }
  };

  // Fetch finances when tab is selected
  useEffect(() => {
    if (activeTab === 'finances' && ticketPurchases.length === 0 && pendingPayouts.count === 0) {
      fetchFinances();
    }
  }, [activeTab]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await getToken();
      const isNewProfile = !profile;
      const response = await fetch(isNewProfile ? '/api/profiles' : '/api/profiles/me', {
        method: isNewProfile ? 'POST' : 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          displayName,
          bio: bio || null,
          instagram: instagram || null,
          website: website || null,
          country: country || null,
                    isModel,
          isArtist,
          isHost,
          modelingStyles,
          languages,
          defaultShowIdentity,
          cameraEquipment: cameraEquipment || null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save profile');
      }

      const data = await response.json();
      const savedProfile = data.profile as Profile;
      await syncPhotoDrafts(profile ?? savedProfile, token);

      const updatedResponse = await fetch('/api/profiles/me', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!updatedResponse.ok) {
        applyProfileToDraft(data.profile);
      } else {
        const updatedData = await updatedResponse.json();
        applyProfileToDraft(updatedData.profile);
      }
      await refreshCurrentProfile();
      setShowSuccess(true);
    } catch (err) {
      console.error('Error saving profile:', err);
      alert('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>, photoType: PhotoType = 'profile') => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const setUploading = photoType === 'profile' ? setUploadingPhotos :
                         photoType === 'space' ? setUploadingSpacePhotos :
                         setUploadingArtwork;
    const inputRef = photoType === 'profile' ? fileInputRef :
                     photoType === 'space' ? spaceFileInputRef :
                     artworkFileInputRef;

    const maxPhotos = photoType === 'space' ? 10 : 20;
    const currentPhotos = getDraftPhotos(photoType);
    const availableSlots = maxPhotos - currentPhotos.length;

    if (availableSlots <= 0) {
      alert(`Maximum photo limit reached (${maxPhotos} ${photoType} photos)`);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      return;
    }

    const newPhotos = files.slice(0, availableSlots).map((file, index) => {
      const url = URL.createObjectURL(file);
      objectUrlsRef.current.push(url);
      return {
        id: `draft-${crypto.randomUUID()}`,
        profileId: profile?.id || 'draft',
        url,
        localUrl: url,
        displayOrder: currentPhotos.length + index,
        photoType,
        createdAt: new Date().toISOString(),
        file,
      };
    });

    setUploading(true);
    updateDraftPhotos(photoType, (photos) => [...photos, ...newPhotos]);
    setUploading(false);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleDeletePhoto = (photoId: string, photoType: PhotoType = 'profile') => {
    if (!confirm('Delete this photo?')) return;

    updateDraftPhotos(photoType, (photos) => {
      const photo = photos.find((item) => item.id === photoId);
      if (photo?.localUrl) {
        URL.revokeObjectURL(photo.localUrl);
        objectUrlsRef.current = objectUrlsRef.current.filter((url) => url !== photo.localUrl);
      }
      return photos.filter((item) => item.id !== photoId);
    });
  };

  const handleReorderPhotos = (photoIds: string[], photoType: PhotoType = 'profile') => {
    updateDraftPhotos(photoType, (photos) => {
      const photosById = new Map(photos.map((photo) => [photo.id, photo]));
      return photoIds
        .map((id, index) => {
          const photo = photosById.get(id);
          return photo ? { ...photo, displayOrder: index } : null;
        })
        .filter((photo): photo is DraftProfilePhoto => photo !== null);
    });
  };

  const handleRotatePhoto = (photoId: string, photoType: PhotoType = 'profile') => {
    updateDraftPhotos(photoType, (photos) => photos.map((photo) => {
      if (photo.id !== photoId) return photo;
      return {
        ...photo,
        rotationDegrees: ((photo.rotationDegrees ?? 0) + 90) % 360,
      };
    }));
  };

  const handleCancel = () => {
    if (profile) {
      applyProfileToDraft(profile);
    }
    setShowSuccess(false);
  };

  const getDraftPhotos = (photoType: PhotoType): DraftProfilePhoto[] => {
    if (photoType === 'space') return draftSpacePhotos;
    if (photoType === 'artwork') return draftArtworkPhotos;
    return draftPhotos;
  };

  const getProfilePhotos = (sourceProfile: Profile, photoType: PhotoType): ProfilePhoto[] => {
    if (photoType === 'space') return sourceProfile.spacePhotos || [];
    if (photoType === 'artwork') return sourceProfile.artworkPhotos || [];
    return sourceProfile.photos || [];
  };

  const updateDraftPhotos = (
    photoType: PhotoType,
    updater: (photos: DraftProfilePhoto[]) => DraftProfilePhoto[]
  ) => {
    if (photoType === 'space') {
      setDraftSpacePhotos(updater);
    } else if (photoType === 'artwork') {
      setDraftArtworkPhotos(updater);
    } else {
      setDraftPhotos(updater);
    }
  };

  const photosChanged = (original: ProfilePhoto[], draft: DraftProfilePhoto[]) => {
    if (draft.some((photo) => photo.file)) return true;
    if (draft.some((photo) => (photo.rotationDegrees ?? 0) !== 0)) return true;
    return original.map((photo) => photo.id).join('|') !== draft.map((photo) => photo.id).join('|');
  };

  const syncPhotoDrafts = async (originalProfile: Profile, token: string | null) => {
    for (const photoType of ['profile', 'space', 'artwork'] as PhotoType[]) {
      await syncPhotoDraft(originalProfile, photoType, getDraftPhotos(photoType), token);
    }
  };

  const syncPhotoDraft = async (
    originalProfile: Profile,
    photoType: PhotoType,
    draft: DraftProfilePhoto[],
    token: string | null
  ) => {
    const original = getProfilePhotos(originalProfile, photoType);
    const draftExistingIds = new Set(draft.filter((photo) => !photo.file).map((photo) => photo.id));
    const deletedIds = original
      .filter((photo) => !draftExistingIds.has(photo.id))
      .map((photo) => photo.id);

    for (const photoId of deletedIds) {
      const response = await fetch(`/api/profiles/me/photos/${photoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete photo');
      }
    }

    const rotatedExistingPhotos = draft.filter(
      (photo) => !photo.file && (photo.rotationDegrees ?? 0) !== 0
    );

    for (const photo of rotatedExistingPhotos) {
      const response = await fetch(`/api/profiles/me/photos/${photo.id}/rotate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ degrees: photo.rotationDegrees }),
      });

      if (!response.ok) {
        throw new Error('Failed to rotate photo');
      }
    }

    const newDraftPhotos = draft.filter((photo) => photo.file);
    const newPhotoIds = new Map<string, string>();

    if (newDraftPhotos.length > 0) {
      const formData = new FormData();
      newDraftPhotos.forEach((photo) => {
        if (photo.file) {
          formData.append('photos', photo.file);
        }
      });
      formData.append('photoType', photoType);
      formData.append('rotations', JSON.stringify(
        newDraftPhotos.map((photo) => photo.rotationDegrees ?? 0)
      ));

      const response = await fetch('/api/profiles/me/photos', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to upload photos');
      }

      const data = await response.json();
      const addedPhotos = (data.addedPhotos || []) as ProfilePhoto[];
      newDraftPhotos.forEach((photo, index) => {
        const addedPhoto = addedPhotos[index];
        if (!addedPhoto) {
          throw new Error('Failed to match uploaded photo');
        }
        newPhotoIds.set(photo.id, addedPhoto.id);
      });
    }

    const orderedPhotoIds = draft
      .map((photo) => newPhotoIds.get(photo.id) || photo.id)
      .filter((id) => !id.startsWith('draft-'));

    if (orderedPhotoIds.length > 0) {
      const response = await fetch('/api/profiles/me/photos/order', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ photoIds: orderedPhotoIds, photoType }),
      });

      if (!response.ok) {
        throw new Error('Failed to reorder photos');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-text-dim">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="bg-panel border border-line rounded-xl p-6 max-w-md text-center">
          <h2 className="text-lg font-medium text-warn mb-2">Error</h2>
          <p className="text-text-dim mb-4">{error}</p>
          <button onClick={() => navigate('/')} className="btn">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const hasChanges =
    displayName !== profile?.displayName ||
    bio !== (profile?.bio || '') ||
    instagram !== (profile?.instagram || '') ||
    website !== (profile?.website || '') ||
    country !== (profile?.country || '') ||
    isModel !== profile?.isModel ||
    isArtist !== profile?.isArtist ||
    isHost !== profile?.isHost ||
    JSON.stringify(modelingStyles) !== JSON.stringify(profile?.modelingStyles || []) ||
    JSON.stringify(languages) !== JSON.stringify(profile?.languages || []) ||
    defaultShowIdentity !== (profile?.defaultShowIdentity ?? true) ||
    cameraEquipment !== (profile?.cameraEquipment || '') ||
    (profile ? photosChanged(profile.photos || [], draftPhotos) : false) ||
    (profile ? photosChanged(profile.spacePhotos || [], draftSpacePhotos) : false) ||
    (profile ? photosChanged(profile.artworkPhotos || [], draftArtworkPhotos) : false);

  const countryName = country ? COUNTRIES.find(c => c.code === country)?.name ?? null : null;
  const savedUsername = profile?.username || '';
  const savedProfilePath = profile ? `/u/${savedUsername || profile.id}` : '';
  const displayNameError = getDisplayNameValidationMessage(displayName);
  const canSave = hasChanges && !displayNameError;
  const selectedLanguages = languages
    .map(code => LANGUAGE_OPTIONS.find(([languageCode]) => languageCode === code))
    .filter((language): language is [LanguageCode, string] => Boolean(language));
  const languageQuery = languageSearch.trim().toLowerCase();
  const availableLanguages = LANGUAGE_OPTIONS
    .filter(([code, name]) => (
      !languages.includes(code) &&
      (
        !languageQuery ||
        name.toLowerCase().includes(languageQuery) ||
        code.toLowerCase().includes(languageQuery)
      )
    ))
    .slice(0, 8);

  const addLanguage = (language: LanguageCode) => {
    if (languages.includes(language)) return;
    setLanguages([...languages, language]);
    setLanguageSearch('');
  };

  const removeLanguage = (language: LanguageCode) => {
    setLanguages(languages.filter(code => code !== language));
  };

  return (
    <div className="min-h-screen flex flex-col bg-bg">
      {/* Header */}
      <header className="border-b border-line chrome-gradient">
        <div className="page-container h-14 flex items-center gap-5">
          <Wordmark />
          <div className="w-px h-5 bg-line" />
          <span className="text-sm text-text-dim font-mono">Edit profile</span>
          <div className="flex-1" />
          <UserMenu />
        </div>
      </header>

      {/* Main Content - Two Column Layout */}
      <div className="flex-1 overflow-auto bg-bg">
        <div className="page-container py-14">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-10 items-start">
            {/* Left: Edit Form */}
            <div className="flex flex-col gap-7">
              {/* Hidden file inputs */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handlePhotoUpload(e, 'profile')}
                className="hidden"
              />
              <input
                ref={spaceFileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handlePhotoUpload(e, 'space')}
                className="hidden"
              />
              <input
                ref={artworkFileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handlePhotoUpload(e, 'artwork')}
                className="hidden"
              />

              {/* Header */}
              <div className="flex items-end justify-between gap-4 flex-wrap">
                <h1 className="page-heading">
                  Profile
                </h1>
                {savedProfilePath && (
                  <Link to={savedProfilePath} className="btn">
                    View profile
                  </Link>
                )}
              </div>

              {/* Tab Navigation */}
              <div className="flex gap-1 border-b border-line">
                <TabButton
                  label="General"
                  active={activeTab === 'general'}
                  onClick={() => setActiveTab('general')}
                />
                {isModel && (
                  <>
                    <TabButton
                      label="Model Profile"
                      active={activeTab === 'model'}
                      onClick={() => setActiveTab('model')}
                    />
                    <TabButton
                      label="Reference Packs"
                      active={activeTab === 'reference-packs'}
                      onClick={() => setActiveTab('reference-packs')}
                    />
                  </>
                )}
                {isArtist && (
                  <TabButton
                    label="Artist Portfolio"
                    active={activeTab === 'artist'}
                    onClick={() => setActiveTab('artist')}
                  />
                )}
                <TabButton
                  label="Finances"
                  active={activeTab === 'finances'}
                  onClick={() => setActiveTab('finances')}
                />
                <TabButton
                  label="Account"
                  active={activeTab === 'account'}
                  onClick={() => setActiveTab('account')}
                />
              </div>

              {/* General Tab */}
              {activeTab === 'general' && (
                <div className="flex flex-col gap-6">
                  {/* Name */}
                  <div className="flex flex-col gap-2">
                    <label className="label-mono">Name</label>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Your name or stage name"
                      className="input text-[15px]"
                    />
                    {displayNameError && (
                      <p className="text-xs text-red-700">{displayNameError}</p>
                    )}
                  </div>

                  {/* Username */}
                  <div className="flex flex-col gap-2">
                    <label className="label-mono">Username</label>
                    <input
                      type="text"
                      value={username}
                      disabled
                      placeholder="No username set"
                      className="input text-[15px] disabled:opacity-70 disabled:cursor-not-allowed"
                    />
                  </div>

                  {/* Bio */}
                  <div className="flex flex-col gap-2">
                    <label className="label-mono">
                      About you <span className="text-text-faint font-normal">(optional)</span>
                    </label>
                    <textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      placeholder="Tell others a bit about yourself..."
                      className="input min-h-[100px] resize-none pt-3 text-[15px]"
                      rows={4}
                    />
                  </div>

                  {/* Profile Photo */}
                  <div className="flex flex-col gap-2">
                    <label className="label-mono">Profile Photo</label>
                    <PhotoGrid
                      photos={draftPhotos.slice(0, 1)}
                      onDelete={(id) => handleDeletePhoto(id, 'profile')}
                      onReorder={(ids) => handleReorderPhotos(ids, 'profile')}
                      onAdd={() => fileInputRef.current?.click()}
                      uploading={uploadingPhotos}
                      maxPhotos={1}
                    />
                    <p className="text-[13px] text-text-faint">
                      Your main profile photo visible across the site.
                    </p>
                  </div>

                  {/* Roles */}
                  <div className="flex flex-col gap-2.5">
                    <div className="label-mono">I am a...</div>
                    <div className="flex flex-wrap gap-2">
                      <RoleToggle
                        label="Model"
                        description="I pose for life drawing sessions"
                        checked={isModel}
                        onChange={setIsModel}
                      />
                      <RoleToggle
                        label="Artist"
                        description="I draw at life drawing sessions"
                        checked={isArtist}
                        onChange={setIsArtist}
                      />
                    </div>
                  </div>

                  {/* Country */}
                  <div className="flex flex-col gap-2">
                    <label className="label-mono">
                      Location <span className="text-text-faint font-normal">(optional)</span>
                    </label>
                    <select
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className="input appearance-none bg-panel pr-10 text-[15px]"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none' stroke='%23666' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M2.5 4.5L6 8L9.5 4.5'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                      }}
                    >
                      <option value="">Select your country</option>
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Instagram */}
                  <div className="flex flex-col gap-2">
                    <label className="label-mono">
                      Instagram <span className="text-text-faint font-normal">(optional)</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-text-dim text-[15px]">@</span>
                      <input
                        type="text"
                        value={instagram}
                        onChange={(e) => setInstagram(e.target.value.replace(/^@/, ''))}
                        placeholder="yourhandle"
                        className="input pl-8 text-[15px]"
                      />
                    </div>
                  </div>

                  {/* Website */}
                  <div className="flex flex-col gap-2">
                    <label className="label-mono">
                      Website <span className="text-text-faint font-normal">(optional)</span>
                    </label>
                    <input
                      type="url"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      placeholder="https://yourwebsite.com"
                      className="input text-[15px]"
                    />
                  </div>

                  {/* Languages */}
                  <div className="flex flex-col gap-2">
                    <label className="label-mono">Languages</label>
                    <div className="space-y-3">
                      {selectedLanguages.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {selectedLanguages.map(([code, name]) => (
                            <span
                              key={code}
                              className="inline-flex items-center gap-2 rounded-full border border-line bg-panel px-3 py-1.5 text-[14px] text-text"
                            >
                              {name}
                              <button
                                type="button"
                                onClick={() => removeLanguage(code)}
                                className="text-text-faint transition-colors hover:text-text"
                                aria-label={`Remove ${name}`}
                              >
                                x
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      <input
                        type="text"
                        value={languageSearch}
                        onChange={(event) => setLanguageSearch(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            const firstMatch = availableLanguages[0]?.[0];
                            if (firstMatch) addLanguage(firstMatch);
                          }

                          if (event.key === 'Backspace' && !languageSearch && languages.length > 0) {
                            event.preventDefault();
                            const lastLanguage = languages[languages.length - 1];
                            if (lastLanguage) removeLanguage(lastLanguage);
                          }
                        }}
                        className="input text-[15px]"
                        placeholder="Search languages to add"
                        aria-describedby="profile-language-search-hint"
                      />

                      {availableLanguages.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {availableLanguages.map(([code, name]) => (
                            <button
                              key={code}
                              type="button"
                              onClick={() => addLanguage(code)}
                              className="rounded-full border border-line px-3 py-1.5 text-[14px] text-text-dim transition-colors hover:border-text/30 hover:text-text"
                            >
                              {name}
                            </button>
                          ))}
                        </div>
                      )}

                      {languageQuery && availableLanguages.length === 0 && (
                        <p className="text-[13px] text-text-faint">No matching languages found.</p>
                      )}
                      <p id="profile-language-search-hint" className="text-[13px] text-text-faint">
                        Type a language and press Enter, or choose one from the suggestions.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Model Tab */}
              {activeTab === 'model' && isModel && (
                <div className="flex flex-col gap-6">
                  {/* View Profile Link */}
                  {profile && (
                    <div className="flex items-center gap-3 p-4 bg-panel2 rounded-lg border border-line">
                      <div className="flex-1">
                        <p className="text-[14px] text-text">
                          Your public profile is live at{' '}
                          <Link to={savedProfilePath} className="text-accent hover:underline font-medium">
                            {savedProfilePath}
                          </Link>
                        </p>
                      </div>
                      <Link
                        to={savedProfilePath}
                        className="btn btn-sm"
                      >
                        View Profile
                      </Link>
                    </div>
                  )}

                  {/* Model Photos */}
                  <div className="flex flex-col gap-2">
                    <label className="label-mono">Model Photos</label>
                    <p className="text-[14px] text-text-dim -mt-1">
                      Photos of yourself that will appear on your model profile. We recommend at least 6 photos.
                    </p>
                    <PhotoGrid
                      photos={draftPhotos}
                      onDelete={(id) => handleDeletePhoto(id, 'profile')}
                      onReorder={(ids) => handleReorderPhotos(ids, 'profile')}
                      onAdd={() => fileInputRef.current?.click()}
                      uploading={uploadingPhotos}
                      maxPhotos={20}
                      masonry
                    />
                    {draftPhotos.length < 6 && (
                      <p className="text-[13px] text-warn">
                        Add at least {6 - draftPhotos.length} more photo{6 - draftPhotos.length !== 1 ? 's' : ''} to complete your profile
                      </p>
                    )}
                  </div>

                  {/* Modeling Styles */}
                  <div className="flex flex-col gap-2">
                    <label className="label-mono">Modeling styles</label>
                    <p className="text-[14px] text-text-dim -mt-1">
                      What type of modeling are you available for?
                    </p>
                    <div className="flex flex-col gap-4 mt-1">
                      {MODELING_STYLE_OPTIONS.map(option => (
                        <div key={option.value} className="flex items-start gap-3">
                          <div
                            onClick={() => {
                              setModelingStyles(prev =>
                                prev.includes(option.value)
                                  ? prev.filter(s => s !== option.value)
                                  : [...prev, option.value]
                              );
                            }}
                            className={`w-5 h-5 mt-0.5 rounded flex-shrink-0 flex items-center justify-center border cursor-pointer transition-colors ${
                              modelingStyles.includes(option.value)
                                ? 'border-accent bg-accent'
                                : 'border-line bg-panel hover:border-text/30'
                            }`}
                          >
                            {modelingStyles.includes(option.value) && (
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                          <div className="flex-1 cursor-pointer" onClick={() => {
                            setModelingStyles(prev =>
                              prev.includes(option.value)
                                ? prev.filter(s => s !== option.value)
                                : [...prev, option.value]
                            );
                          }}>
                            <span className="text-[15px] text-text font-medium">{option.label}</span>
                            <p className="text-[13px] text-text-dim mt-0.5">{option.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Privacy Defaults */}
                  <ModelPrivacyDefaults
                    showIdentity={defaultShowIdentity}
                    onShowIdentityChange={setDefaultShowIdentity}
                  />

                  {/* Space Photos */}
                  <div className="flex flex-col gap-2 pt-6 border-t border-line-soft">
                    <label className="label-mono">Your Posing Space</label>
                    <p className="text-[14px] text-text-dim -mt-1">
                      Show hosts where you typically pose from. Add at least one photo of your setup.
                    </p>
                    <PhotoGrid
                      photos={draftSpacePhotos}
                      onDelete={(id) => handleDeletePhoto(id, 'space')}
                      onReorder={(ids) => handleReorderPhotos(ids, 'space')}
                      onAdd={() => spaceFileInputRef.current?.click()}
                      uploading={uploadingSpacePhotos}
                      maxPhotos={10}
                    />
                    {draftSpacePhotos.length < 1 && (
                      <p className="text-[13px] text-warn">
                        Add one photo of your pose area
                      </p>
                    )}
                  </div>

                  {/* Camera Equipment */}
                  <div className="flex flex-col gap-2">
                    <label className="label-mono">
                      Camera & Equipment <span className="text-text-faint font-normal">(optional)</span>
                    </label>
                    <p className="text-[14px] text-text-dim -mt-1">
                      Describe your camera, lighting, and streaming setup.
                    </p>
                    <textarea
                      value={cameraEquipment}
                      onChange={(e) => setCameraEquipment(e.target.value)}
                      placeholder="e.g., Logitech C920 webcam, ring light, neutral background..."
                      className="input min-h-[80px] resize-none pt-3 text-[15px]"
                      rows={3}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'reference-packs' && isModel && (
                <div className="flex flex-col gap-8">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h2 className="text-[26px] font-light text-text">{t('referencePacks.modelQueue.pageTitle')}</h2>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-[16px] font-medium text-text">{t('referencePacks.modelQueue.heading')}</h3>
                      {loadingAccessRequests && (
                        <span className="text-[12px] text-text-faint">{t('referencePacks.modelQueue.loading')}</span>
                      )}
                    </div>

                    {accessRequests.length === 0 ? (
                      <div className="p-5 bg-panel2 border border-line rounded-lg text-[14px] text-text-dim">
                        {t('referencePacks.modelQueue.empty')}
                      </div>
                    ) : (
                      accessRequests.map((request) => {
                        const draft = accessRequestDrafts[request.id] ?? {
                          price: centsToPriceInput(request.approvedPriceCents ?? request.requestedPriceCents ?? request.product.priceCents),
                          note: request.sellerNote ?? '',
                        };
                        const isPending = request.status === 'pending';
                        const isReviewing = reviewingRequestId === request.id;

                        return (
                          <div key={request.id} className="p-4 bg-panel2 border border-line rounded-lg">
                            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="text-[15px] font-medium text-text">{request.product.title}</div>
                                  <span className="text-[11px] uppercase tracking-[0.12em] text-text-faint">{request.status}</span>
                                </div>
                                <div className="text-[13px] text-text-dim mt-1">
                                  {t('referencePacks.modelQueue.listedPrice', {
                                    buyerName: request.buyerProfile.displayName || t('referencePacks.modelQueue.buyerFallback'),
                                    roomName: request.room.name,
                                    price: formatCents(request.requestedPriceCents ?? request.product.priceCents, request.product.currency),
                                  })}
                                </div>
                                <p className="mt-3 text-[14px] leading-relaxed text-text">
                                  {request.intendedUse}
                                </p>
                                {request.requestedAccessExpiresOn && (
                                  <div className="mt-2 text-[12px] text-text-dim">
                                    {t('referencePacks.modelQueue.requestedAccessUntil', { date: request.requestedAccessExpiresOn })}
                                  </div>
                                )}
                                {!isPending && (
                                  <div className="mt-2 text-[12px] text-text-dim">
                                    {request.status === 'approved'
                                      ? t('referencePacks.modelQueue.approvedAt', { price: formatCents(request.approvedPriceCents, request.product.currency) })
                                      : t('referencePacks.modelQueue.declined')}
                                    {request.sellerNote ? ` · ${request.sellerNote}` : ''}
                                  </div>
                                )}
                              </div>

                              {isPending && (
                                <div className="w-full lg:w-[260px] flex flex-col gap-2">
                                  <label className="block">
                                    <span className="text-[12px] font-medium text-text-dim">{t('referencePacks.modelQueue.acceptedPrice')}</span>
                                    <div className="mt-1 flex items-center gap-2">
                                      <span className="text-[14px] text-text-dim">$</span>
                                      <input
                                        type="number"
                                        min="0.50"
                                        step="0.01"
                                        value={draft.price}
                                        onChange={(event) => updateAccessRequestDraft(request.id, 'price', event.target.value)}
                                        className="input h-10 text-[14px]"
                                      />
                                    </div>
                                  </label>
                                  <label className="block">
                                    <span className="text-[12px] font-medium text-text-dim">{t('referencePacks.modelQueue.note')}</span>
                                    <textarea
                                      value={draft.note}
                                      onChange={(event) => updateAccessRequestDraft(request.id, 'note', event.target.value)}
                                      className="input mt-1 min-h-[76px] h-auto py-2 text-[13px] resize-y"
                                      placeholder={t('referencePacks.modelQueue.notePlaceholder')}
                                    />
                                  </label>
                                  <div className="flex justify-end gap-2">
                                    <button
                                      type="button"
                                      onClick={() => denyAccessRequest(request)}
                                      disabled={isReviewing}
                                      className="btn btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {t('common.deny')}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => approveAccessRequest(request)}
                                      disabled={isReviewing}
                                      className="btn btn-sm btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {isReviewing ? t('common.saving') : t('common.approve')}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="flex flex-col gap-3">
                    <h3 className="text-[16px] font-medium text-text">{t('referencePacks.modelQueue.publishedPacks')}</h3>
                    {referencePacks.length === 0 ? (
                      <div className="p-5 bg-panel2 border border-line rounded-lg text-[14px] text-text-dim">
                        {t('referencePacks.modelQueue.noPublishedPacks')}
                      </div>
                    ) : (
                      referencePacks.map((pack) => (
                        <div key={pack.id} className="p-4 bg-panel2 border border-line rounded-lg flex items-center justify-between gap-4">
                          <div className="min-w-0">
                            <div className="text-[15px] font-medium text-text">{pack.title}</div>
                            <div className="text-[13px] text-text-dim mt-1">
                              {pack.room.name} · {pack.status} · {pack.showOnProfile ? t('referencePacks.modelQueue.shownOnProfile') : t('referencePacks.modelQueue.hiddenFromProfile')}
                            </div>
                          </div>
                          <div className="flex shrink-0 flex-wrap justify-end gap-2">
                            <Link to={`/s/${pack.room.code}/packs`} className="btn btn-sm">
                              {t('common.edit')}
                            </Link>
                            <button
                              type="button"
                              onClick={() => deleteReferencePack(pack)}
                              disabled={deletingReferencePackId === pack.id}
                              className="btn btn-sm text-warn border-warn/40 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {deletingReferencePackId === pack.id ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Artist Tab */}
              {activeTab === 'artist' && isArtist && (
                <div className="flex flex-col gap-6">
                  {/* View Profile Link */}
                  {profile && (
                    <div className="flex items-center gap-3 p-4 bg-panel2 rounded-lg border border-line">
                      <div className="flex-1">
                        <p className="text-[14px] text-text">
                          Your artist portfolio is visible on your profile at{' '}
                          <Link to={savedProfilePath} className="text-accent hover:underline font-medium">
                            {savedProfilePath}
                          </Link>
                        </p>
                      </div>
                      <Link
                        to={savedProfilePath}
                        className="btn btn-sm"
                      >
                        View Profile
                      </Link>
                    </div>
                  )}

                  <div>
                    <p className="text-[15px] text-text-dim">
                      Showcase artwork you've created. Artwork you share during sessions will also appear here.
                    </p>
                  </div>

                  <div className="flex flex-col gap-2">
                    <PhotoGrid
                      photos={draftArtworkPhotos}
                      onDelete={(id) => handleDeletePhoto(id, 'artwork')}
                      onReorder={(ids) => handleReorderPhotos(ids, 'artwork')}
                      onRotate={(id) => handleRotatePhoto(id, 'artwork')}
                      onAdd={() => artworkFileInputRef.current?.click()}
                      uploading={uploadingArtwork}
                      maxPhotos={20}
                      columns={4}
                      aspectRatio="aspect-square"
                    />
                  </div>
                </div>
              )}

              {/* Finances Tab */}
              {activeTab === 'finances' && (
                <div className="flex flex-col gap-8">
                  {/* Stripe Connect Setup */}
                  <StripeConnectSection
                    pendingPayoutTotal={pendingPayouts.total}
                    pendingPayoutCount={pendingPayouts.count}
                    pendingPayoutsByCurrency={pendingPayouts.byCurrency}
                    getToken={getToken}
                    onPayoutsProcessed={fetchFinances}
                  />

                  {/* Pending Payouts */}
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                      <label className="label-mono">Pending Payouts</label>
                      {loadingFinances && (
                        <span className="w-4 h-4 border-2 border-text-dim/30 border-t-text-dim rounded-full animate-spin" />
                      )}
                    </div>
                    {pendingPayouts.count > 0 ? (
                      <div className="flex items-center justify-between gap-4 p-4 bg-panel2 rounded-lg border border-line">
                        <div className="min-w-0">
                          <div className="text-[15px] font-medium text-text">
                            {pendingPayouts.count} pending payout{pendingPayouts.count === 1 ? '' : 's'}
                          </div>
                          <div className="text-[13px] text-text-dim mt-0.5">
                            Set up Stripe when you are ready to receive this money.
                          </div>
                          {pendingPayouts.byCurrency.length > 1 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {pendingPayouts.byCurrency.map((entry) => (
                                <span key={entry.currency} className="rounded-full border border-line bg-panel px-3 py-1 text-[13px] text-text">
                                  {formatMoney(entry.total, entry.currency)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="text-[18px] font-medium text-ok">
                          {pendingPayouts.byCurrency.length > 1
                            ? 'Multiple currencies'
                            : formatMoney(pendingPayouts.total, pendingPayouts.byCurrency[0]?.currency)}
                        </div>
                      </div>
                    ) : (
                      <p className="text-[14px] text-text-dim">
                        {loadingFinances ? 'Loading...' : 'No pending payouts'}
                      </p>
                    )}
                  </div>

                  {/* Ticket Purchases */}
                  <div className="flex flex-col gap-4">
                    <label className="label-mono">Ticket Purchases</label>
                    {ticketPurchases.length > 0 ? (
                      <div className="flex flex-col gap-2">
                        {ticketPurchases.map((ticket) => (
                          <Link
                            key={ticket.id}
                            to={`/s/${ticket.roomCode}`}
                            className="flex items-center justify-between p-4 bg-panel2 rounded-lg border border-line hover:border-text/20 transition-colors"
                          >
                            <div>
                              <div className="text-[15px] font-medium text-text">{ticket.roomName}</div>
                              <div className="text-[13px] text-text-dim mt-0.5">
                                {ticket.scheduledAt
                                  ? new Date(ticket.scheduledAt).toLocaleDateString(undefined, {
                                      weekday: 'short',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: 'numeric',
                                      minute: '2-digit',
                                    })
                                  : 'Date TBD'}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-[15px] font-medium text-text">
                                ${(ticket.amount / 100).toFixed(2)}
                              </div>
                              <div className="text-[12px] text-text-dim capitalize">{ticket.status}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[14px] text-text-dim">
                        {loadingFinances ? 'Loading...' : 'No ticket purchases yet'}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'account' && (
                <AccountDeletionPanel />
              )}

              {/* Buttons */}
              {activeTab !== 'account' && (
                <div className="flex items-center gap-2.5 mt-auto pt-[18px] border-t border-line-soft">
                  <button onClick={handleCancel} disabled={!hasChanges || saving} className="btn disabled:opacity-50">
                    Cancel
                  </button>
                  <div className="flex-1" />
                  <button
                    onClick={handleSave}
                    disabled={saving || !canSave}
                    className="btn btn-accent btn-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              )}

              {/* Success message */}
              {showSuccess && (
                <div className="bg-ok/10 border border-ok/30 rounded-lg px-4 py-3 flex items-center gap-3">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-ok">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span className="text-ok text-[14px]">Profile saved successfully!</span>
                </div>
              )}
            </div>

            {/* Right: Preview Panel */}
            <aside className="lg:sticky lg:top-6">
              <div className="mb-3">
                <div className="font-mono text-[12px] text-text-faint uppercase font-semibold">Preview</div>
              </div>
              <ProfilePreviewCard
                displayName={displayName}
                bio={bio}
                photos={draftPhotos}
                country={country}
                countryName={countryName}
                instagram={instagram}
                website={website}
                isModel={isModel}
                isArtist={isArtist}
                isHost={isHost}
                upcomingSessions={upcomingSessions}
                showIdentity={defaultShowIdentity}
              />
            </aside>
          </div>
        </div>
      </div>
      <SharedFooter />
    </div>
  );
}

interface ProfilePreviewCardProps {
  displayName: string;
  bio: string;
  photos: ProfilePhoto[];
  country: string;
  countryName: string | null;
  instagram: string;
  website: string;
  isModel: boolean;
  isArtist: boolean;
  isHost: boolean;
  upcomingSessions: UpcomingSession[];
  showIdentity: boolean;
}

function ProfilePreviewCard({
  displayName,
  bio,
  photos,
  country,
  countryName,
  instagram,
  website,
  isModel,
  isArtist,
  isHost,
  upcomingSessions,
  showIdentity,
}: ProfilePreviewCardProps) {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const primaryPhoto = photos[selectedPhotoIndex] || photos[0];
  const hasRoles = isModel || isArtist || isHost;

  // Filter out modeling sessions if showIdentity is disabled
  const visibleSessions = showIdentity
    ? upcomingSessions
    : upcomingSessions.filter(s => s.role !== 'modeling');

  return (
    <div className="bg-panel border border-line rounded-[20px] shadow-xl overflow-hidden">
      {/* Hero Photo */}
      <div className="relative aspect-[4/5] bg-panel2 overflow-hidden">
        {primaryPhoto ? (
          <>
            <img
              src={primaryPhoto.url}
              alt={displayName || 'Profile'}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-text-faint">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className="mx-auto mb-2 opacity-40">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
              <span className="text-sm">No photos yet</span>
            </div>
          </div>
        )}

        {/* Role badges */}
        {hasRoles && (
          <div className="absolute top-4 left-4 flex flex-wrap gap-1.5">
            {isModel && (
              <span className="px-2.5 py-1 bg-tag/90 text-white text-[10px] font-semibold uppercase rounded-md backdrop-blur-sm">
                Model
              </span>
            )}
            {isArtist && (
              <span className="px-2.5 py-1 bg-tag/80 text-white text-[10px] font-semibold uppercase rounded-md backdrop-blur-sm">
                Artist
              </span>
            )}
            {isHost && (
              <span className="px-2.5 py-1 bg-accent/90 text-white text-[10px] font-semibold uppercase rounded-md backdrop-blur-sm">
                Host
              </span>
            )}
          </div>
        )}

        {/* Name overlay */}
        <div className="absolute bottom-4 left-5 right-5 z-10">
          <h2 className="font-display text-[28px] font-normal text-white leading-tight" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
            {displayName ? (
              displayName
            ) : (
              <span className="opacity-50">Your name</span>
            )}
          </h2>
          {country && countryName && (
            <div className="flex items-center gap-1.5 mt-1.5 text-white/80 text-[13px]" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>
              <span className="text-base">{getCountryFlag(country)}</span>
              <span>{countryName}</span>
            </div>
          )}
        </div>
      </div>

      {/* Photo thumbnails */}
      {photos.length > 1 && (
        <div className="px-4 py-3 border-b border-line-soft bg-panel2/50">
          <div className="flex gap-2 overflow-x-auto">
            {photos.map((photo, index) => (
              <button
                key={photo.id}
                onClick={() => setSelectedPhotoIndex(index)}
                className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                  index === selectedPhotoIndex
                    ? 'border-accent shadow-md'
                    : 'border-transparent opacity-70 hover:opacity-100'
                }`}
              >
                <img
                  src={photo.url}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Body */}
      <div className="p-6">
        {/* Bio */}
        {bio ? (
          <p className="text-[15px] text-text-dim leading-relaxed">
            {bio}
          </p>
        ) : (
          <p className="text-[15px] text-text-faint italic">
            No bio provided
          </p>
        )}

        {/* Links */}
        {(instagram || website) && (
          <div className="flex flex-col gap-2.5 mt-5 pt-5 border-t border-line-soft">
            {instagram && (
              <a
                href={`https://instagram.com/${instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-[14px] text-text-dim hover:text-text transition-colors group"
              >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <span className="group-hover:underline">@{instagram}</span>
              </a>
            )}
            {website && (
              <a
                href={website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-[14px] text-text-dim hover:text-text transition-colors group"
              >
                <div className="w-8 h-8 rounded-lg bg-panel2 border border-line flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-dim">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                </div>
                <span className="group-hover:underline truncate">{website.replace(/^https?:\/\//, '')}</span>
              </a>
            )}
          </div>
        )}

        {/* Upcoming Sessions */}
        {visibleSessions.length > 0 && (
          <div className="mt-5 pt-5 border-t border-line-soft space-y-1">
            <div className="font-mono text-[10px] text-text-faint uppercase font-semibold mb-2">
              Upcoming Sessions
            </div>
            {visibleSessions.map(session => (
              <SessionItem key={session.room.id} session={session} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SessionItem({ session }: { session: UpcomingSession }) {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const rolePillStyles = {
    hosting: 'bg-accent/90 text-white',
    modeling: 'bg-tag/90 text-white',
    attending: 'bg-tag/80 text-white',
  };

  const roleLabel = {
    hosting: 'Host',
    modeling: 'Model',
    attending: 'Artist',
  };

  return (
    <Link
      to={`/s/${session.room.code}`}
      className="flex items-center gap-3 p-3 -mx-1 rounded-lg hover:bg-panel2 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-text line-clamp-1">
          {session.room.name}
        </div>
        <div className="text-[11px] text-text-dim mt-0.5">
          {formatDate(session.room.scheduledAt)}
        </div>
      </div>
      <span className={`flex-shrink-0 px-2 py-0.5 text-[9px] font-semibold uppercase rounded-md ${rolePillStyles[session.role]}`}>
        {roleLabel[session.role]}
      </span>
    </Link>
  );
}

interface PhotoGridProps {
  photos: DraftProfilePhoto[];
  onDelete: (id: string) => void;
  onReorder: (ids: string[]) => void;
  onRotate?: (id: string) => void;
  onAdd: () => void;
  uploading: boolean;
  maxPhotos?: number;
  columns?: number;
  aspectRatio?: string;
  masonry?: boolean;
}

function PhotoGrid({ photos, onDelete, onReorder, onRotate, onAdd, uploading, maxPhotos = 12, columns = 3, aspectRatio = 'aspect-[3/4]', masonry = false }: PhotoGridProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [imageDimensions, setImageDimensions] = useState<Record<string, { width: number; height: number }>>({});

  // Load image dimensions for lightbox
  useEffect(() => {
    photos.forEach((photo) => {
      if (!imageDimensions[photo.id]) {
        const img = new Image();
        img.onload = () => {
          setImageDimensions((prev) => ({
            ...prev,
            [photo.id]: { width: img.naturalWidth, height: img.naturalHeight },
          }));
        };
        img.src = photo.url;
      }
    });
  }, [photos]);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDragIndex(index);
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const startIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);

    if (startIndex === dropIndex) {
      setDragIndex(null);
      return;
    }

    const newPhotos = [...photos];
    const [draggedPhoto] = newPhotos.splice(startIndex, 1);
    newPhotos.splice(dropIndex, 0, draggedPhoto);

    onReorder(newPhotos.map((p) => p.id));
    setDragIndex(null);
  };

  const canAddMore = photos.length < maxPhotos;

  // Justified rows layout using react-photo-album
  if (masonry) {
    // Build photo album data with dimensions
    const albumPhotos = photos.map((photo) => {
      const dims = imageDimensions[photo.id] || { width: 4, height: 5 };
      return {
        src: photo.url,
        width: dims.width,
        height: dims.height,
        key: photo.id,
        id: photo.id,
        rotationDegrees: photo.rotationDegrees ?? 0,
      };
    });

    return (
      <div className="space-y-3">
        <Gallery withCaption={false}>
          <RowsPhotoAlbum
            photos={albumPhotos}
            targetRowHeight={200}
            spacing={10}
            rowConstraints={{ maxPhotos: 4 }}
            render={{
              image: (props, { photo, index }) => (
                <Item
                  key={photo.key}
                  original={photo.src}
                  thumbnail={photo.src}
                  width={photo.width}
                  height={photo.height}
                >
                  {({ ref, open }) => (
                    <div ref={ref} className="relative group">
                      <img
                        {...props}
                        onClick={open}
                        className="cursor-pointer transition-all duration-300 hover:brightness-95"
                        style={{
                          ...props.style,
                          borderRadius: '10px',
                          transform: `rotate(${photo.rotationDegrees ?? 0}deg)`,
                        }}
                      />
                      {index === 0 && (
                        <div className="absolute top-2 left-2 px-2 py-1 bg-accent/90 text-white text-[10px] font-medium rounded">
                          Primary
                        </div>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(photo.id);
                        }}
                        className="absolute top-2 right-2 w-7 h-7 bg-black/50 hover:bg-black/70 rounded-lg flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                      {onRotate && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRotate(photo.id);
                          }}
                          className="absolute top-2 right-10 w-7 h-7 bg-black/50 hover:bg-black/70 rounded-lg flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Rotate 90 degrees"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 12a9 9 0 1 1-3-6.7" />
                            <path d="M21 3v6h-6" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                </Item>
              ),
            }}
          />
        </Gallery>
        {canAddMore && (
          <button
            onClick={onAdd}
            disabled={uploading}
            className="w-full h-[100px] rounded-xl border-2 border-dashed border-line hover:border-text/30 flex items-center justify-center gap-2 text-text-dim hover:text-text transition-colors disabled:opacity-50"
          >
            {uploading ? (
              <span className="w-6 h-6 border-2 border-text-dim/30 border-t-text-dim rounded-full animate-spin" />
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
                <span className="text-[13px]">Add photos</span>
              </>
            )}
          </button>
        )}
      </div>
    );
  }

  // Standard grid layout with lightbox
  const gridClass = columns === 4 ? 'grid-cols-4' : 'grid-cols-3';

  return (
    <Gallery withCaption={false}>
      <div className={`grid ${gridClass} gap-3`}>
        {photos.map((photo, index) => {
          const dims = imageDimensions[photo.id] || { width: 800, height: 1200 };
          return (
            <Item
              key={photo.id}
              original={photo.url}
              thumbnail={photo.url}
              width={dims.width}
              height={dims.height}
            >
              {({ ref, open }) => (
                <div
                  ref={ref}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  className={`relative ${aspectRatio} rounded-xl overflow-hidden border border-line bg-panel group cursor-pointer ${
                    dragIndex === index ? 'opacity-50' : ''
                  }`}
                >
                  <img
                    src={photo.url}
                    alt={`Photo ${index + 1}`}
                    className="w-full h-full object-cover"
                    style={{ transform: `rotate(${photo.rotationDegrees ?? 0}deg)` }}
                    onClick={open}
                  />
                  {index === 0 && (
                    <div className="absolute top-2 left-2 px-2 py-1 bg-accent/90 text-white text-[10px] font-medium rounded">
                      Primary
                    </div>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(photo.id);
                    }}
                    className="absolute top-2 right-2 w-7 h-7 bg-black/50 hover:bg-black/70 rounded-lg flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                  {onRotate && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRotate(photo.id);
                      }}
                      className="absolute top-2 right-10 w-7 h-7 bg-black/50 hover:bg-black/70 rounded-lg flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Rotate 90 degrees"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 12a9 9 0 1 1-3-6.7" />
                        <path d="M21 3v6h-6" />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </Item>
          );
        })}

        {canAddMore && (
          <button
            onClick={onAdd}
            disabled={uploading}
            className={`${aspectRatio} rounded-xl border-2 border-dashed border-line hover:border-text/30 flex flex-col items-center justify-center gap-2 text-text-dim hover:text-text transition-colors disabled:opacity-50`}
          >
            {uploading ? (
              <span className="w-6 h-6 border-2 border-text-dim/30 border-t-text-dim rounded-full animate-spin" />
            ) : (
              <>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
                <span className="text-[13px]">Add photo</span>
              </>
            )}
          </button>
        )}
      </div>
    </Gallery>
  );
}

interface RoleToggleProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function RoleToggle({ label, description, checked, onChange }: RoleToggleProps) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
        checked
          ? 'border-accent/40 bg-accent-soft text-accent'
          : 'border-line bg-panel text-text-dim hover:border-text/30'
      }`}
    >
      <div
        className={`w-4 h-4 rounded flex items-center justify-center border ${
          checked
            ? `border-current bg-current`
            : 'border-line'
        }`}
      >
        {checked && (
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
      <span className="text-[14px] font-medium">{label}</span>
    </button>
  );
}

interface TabButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function TabButton({ label, active, onClick }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-[14px] font-medium border-b-2 transition-colors -mb-px ${
        active
          ? 'border-accent text-text'
          : 'border-transparent text-text-dim hover:text-text'
      }`}
    >
      {label}
    </button>
  );
}

// Stripe Connect section for hosts and models to receive payouts
interface StripeConnectSectionProps {
  pendingPayoutTotal: number;
  pendingPayoutCount: number;
  pendingPayoutsByCurrency: Array<{ currency: string; total: number; count: number }>;
  getToken: () => Promise<string | null>;
  onPayoutsProcessed: () => void;
}

function StripeConnectSection({
  pendingPayoutTotal,
  pendingPayoutCount,
  pendingPayoutsByCurrency,
  getToken,
  onPayoutsProcessed,
}: StripeConnectSectionProps) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{
    hasAccount: boolean;
    onboardingComplete: boolean;
  } | null>(null);
  const processedPendingRef = useRef(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = await getToken();
      const response = await fetch('/api/stripe/connect/status', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStatus({
          hasAccount: data.hasAccount,
          onboardingComplete: data.onboardingComplete,
        });
      }
    } catch (error) {
      console.error('Error fetching Stripe status:', error);
    }
  };

  const processPendingPayouts = async () => {
    if (processedPendingRef.current || pendingPayoutCount <= 0) return;

    processedPendingRef.current = true;
    try {
      const token = await getToken();
      const response = await fetch('/api/stripe/process-pending-payouts', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error('Failed to process pending payouts');
      }
      onPayoutsProcessed();
    } catch (error) {
      console.error('Error processing pending payouts:', error);
      processedPendingRef.current = false;
    }
  };

  useEffect(() => {
    if (status?.onboardingComplete && pendingPayoutCount > 0) {
      processPendingPayouts();
    }
  }, [status?.onboardingComplete, pendingPayoutCount]);

  const handleConnect = async () => {
    setLoading(true);
    try {
      const token = await getToken();

      // Create account if needed
      if (!status?.hasAccount) {
        const createRes = await fetch('/api/stripe/connect/create-account', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!createRes.ok) throw new Error('Failed to create Stripe account');
      }

      // Create account link for onboarding
      const linkRes = await fetch('/api/stripe/connect/create-account-link', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          returnUrl: `${window.location.origin}/profile?tab=finances&stripe=success`,
          refreshUrl: `${window.location.origin}/profile?tab=finances&stripe=refresh`,
        }),
      });

      if (!linkRes.ok) throw new Error('Failed to create onboarding link');

      const { url } = await linkRes.json();
      window.location.href = url;
    } catch (error) {
      console.error('Error connecting to Stripe:', error);
      alert('Failed to connect to Stripe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDashboard = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await fetch('/api/stripe/connect/create-login-link', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to create dashboard link');

      const { url } = await response.json();
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error opening Stripe dashboard:', error);
      alert('Failed to open Stripe dashboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (pendingPayoutCount <= 0 && !status?.onboardingComplete) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 pt-4 border-t border-line-soft mt-2">
      <div className="flex items-center justify-between">
        <div>
          <label className="label-mono">
            Payouts
          </label>
          <p className="text-[14px] text-text-dim mt-1">
            {pendingPayoutCount > 0
              ? `You have ${pendingPayoutsByCurrency.length > 1 ? 'money in multiple currencies' : formatMoney(pendingPayoutTotal, pendingPayoutsByCurrency[0]?.currency)} waiting across ${pendingPayoutCount} payout${pendingPayoutCount === 1 ? '' : 's'}.`
              : 'Stripe is connected for future payouts.'}
          </p>
        </div>
      </div>

      {pendingPayoutsByCurrency.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {pendingPayoutsByCurrency.map((entry) => (
            <span key={entry.currency} className="rounded-full border border-line bg-panel2 px-3 py-1 text-[13px] text-text">
              {formatMoney(entry.total, entry.currency)}
            </span>
          ))}
        </div>
      )}

      {status?.onboardingComplete ? (
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-ok text-sm">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Stripe connected
          </div>
          <button
            onClick={handleOpenDashboard}
            disabled={loading}
            className="btn btn-sm"
          >
            {loading ? 'Loading...' : 'Open Dashboard'}
          </button>
        </div>
      ) : status?.hasAccount ? (
        <button
          onClick={handleConnect}
          disabled={loading}
          className="btn btn-accent w-fit"
        >
          {loading ? (
            <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Loading...
            </>
          ) : (
            'Complete Stripe Setup'
          )}
        </button>
      ) : (
        <button
          onClick={handleConnect}
          disabled={loading}
          className="btn btn-accent w-fit"
        >
          {loading ? (
            <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Loading...
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                <polyline points="13 2 13 9 20 9" />
              </svg>
              Connect with Stripe
            </>
          )}
        </button>
      )}
    </div>
  );
}
